import { User } from "./UserTypes";
import { toWallClock } from "../../utils/schedule/wallClock";
import type { UserUuid } from "../identity";

export interface TeamMember extends User {
	team_member_info: TeamMemberInfo;
	pivot?: Pivot;
}

// Time block for granular availability scheduling
export interface TimeBlock {
	id: string; // Unique identifier for React keys
	start: string | null;
	end: string | null;
}

// Availability schedule supporting multiple blocks per day
export interface AvailabilitySchedule {
	[day: string]: TimeBlock[];
}

// healthcare's practitioners.id (TeamMemberInfo.id): a record id, never a user reference.
export type PractitionerRecordId = number;

//this is extended by the user interface
export interface TeamMemberInfo {
	id: PractitionerRecordId;
	user_id: UserUuid;
	medical_license_number: string;
	npi_number?: string;
	dea_number?: string;
	specialties?: string[];
	years_of_experience?: number;
	qualifications?: string[];
	certifications?: string[];
	professional_memberships?: string[];
	practicing_locations?: string[];
	availability_schedule?: AvailabilitySchedule | null;
	consultation_fee?: number;
	biography?: string;
	languages_spoken?: string[];
	education_history?: any;
	linkage?: string;
	malpractice_insurance_details?: any;
	billing_information?: any;
	// healthcare validates it as nullable|integer.
	supervisor_id?: PractitionerRecordId | null;
	taxonomy_code?: string;
	emergency_contact?: any;

	// Cliniko integration fields
	cliniko_id?: string;
	cliniko_data?: Record<string, any>;
	cliniko_synced_at?: string;

	color?: string | null;

	created_at: string;
	updated_at: string;
}

export interface Pivot {
	user_id: UserUuid;
	practitioner_id: UserUuid;
	role: string;
}


export const defaultTeamMemberInfo: TeamMemberInfo = {
	id: 0,
	user_id: "",
	medical_license_number: "",
	npi_number: "",
	dea_number: "",
	specialties: [],
	years_of_experience: 0,
	qualifications: [],
	certifications: [],
	professional_memberships: [],
	practicing_locations: [],
	availability_schedule: null,
	consultation_fee: 0,
	biography: "",
	languages_spoken: [],
	education_history: null,
	malpractice_insurance_details: null,
	billing_information: null,
	supervisor_id: undefined,
	taxonomy_code: "",
	emergency_contact: null,
	cliniko_id: undefined,
	cliniko_data: undefined,
	cliniko_synced_at: undefined,
	color: null,
	created_at: new Date().toISOString(),
	updated_at: new Date().toISOString(),
}

export const defaultTeamMember: TeamMember = {
	id: "",
	uuid: "",
	username: "",
	email: "",
	password: "",
	password_confirmation: "",
	first_name: "",
	middle_name: "",
	last_name: "",
	phone: "",
	address: "",
	city: "",
	state: "",
	country: "",
	postal_code: "",
	profile_picture: "",
	date_of_birth: new Date(),
	gender: "",
	sex: "",
	preferences: "",
	is_active: false,
	created_at: new Date(),
	updated_at: new Date(),
	panel_key: "",
	reagents: "",
	roles: [],
	team_member_info: defaultTeamMemberInfo,
	pivot: undefined,
	height: "",
	weight: ""
};
// Add this to src/types/admin/TeamTypes.ts if it doesn't exist

/**
 * Generate a UUID v4 compatible string
 */
function generateUUID(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = Math.random() * 16 | 0;
		const v = c === 'x' ? r : ((r & 0x3) | 0x8);
		return v.toString(16);
	});
}

/**
 * Migrate legacy schedule format (single start/end per day) to new multi-block format
 * @param legacySchedule - Schedule with { start: Date, end: Date } per day
 * @returns Multi-block schedule format
 */
export function migrateLegacySchedule(legacySchedule: any): AvailabilitySchedule {
	const multiBlockSchedule: AvailabilitySchedule = {};
	const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	for (const day of days) {
		const dayData = legacySchedule[day];

		if (!dayData) {
			multiBlockSchedule[day] = [];
			continue;
		}

		// Check if it's the legacy format with start/end properties
		if (typeof dayData === 'object' && ('start' in dayData || 'end' in dayData)) {
			// Only create a block if both start and end exist
			if (dayData.start && dayData.end) {
				const start = toWallClock(dayData.start);
				const end = toWallClock(dayData.end);

				multiBlockSchedule[day] = start && end ? [{
					id: generateUUID(),
					start: start,
					end: end
				}] : [];
			} else {
				multiBlockSchedule[day] = [];
			}
		} else if (Array.isArray(dayData)) {
			// Already in multi-block format
			multiBlockSchedule[day] = dayData.map((block: any) => ({
				id: block.id || generateUUID(),
				start: toWallClock(block.start),
				end: toWallClock(block.end)
			}));
		} else {
			multiBlockSchedule[day] = [];
		}
	}

	return multiBlockSchedule;
}

/**
 * Create a new empty time block
 */
export function createEmptyTimeBlock(): TimeBlock {
	return {
		id: generateUUID(),
		start: null,
		end: null
	};
}

// Add new interfaces
export interface ScheduleOverride {
	id: number;
	user_id: UserUuid;
	start_date: string; // ISO date string
	end_date: string; // ISO date string
	schedule_data: TimeBlock[] | null; // null means blocked
	override_type: 'block' | 'custom' | 'holiday';
	reason?: string;
	created_at: string;
	updated_at: string;
}

export interface EffectiveScheduleResponse {
	date: string;
	schedule: TimeBlock[] | null;
	is_override: boolean;
	override_type?: 'block' | 'custom' | 'holiday';
	override_reason?: string;
}

export interface UnavailableBlock {
	id: number;
	user_id: UserUuid | null; // null = a clinic-wide closure
	// ISO-8601 UTC instant, NOT TimeBlock.start's clinic-local "HH:mm" — the two sit side by side here.
	starts_at: string;
	ends_at: string;
	reason?: string | null;
	block_type: string;
	created_by: UserUuid;
	created_at: string;
	updated_at: string;
}

// starts_at/ends_at must carry an explicit offset — a bare "2026-09-01 10:00:00" is a 422.
export interface UnavailableBlockPayload {
	starts_at: string;
	ends_at: string;
	block_type: string;
	reason?: string | null;
}

// The bulk-read envelope verbatim. clinic_closures is returned once, never fanned out per
// practitioner — merging it into a bucket double-counts every closure in the calendar.
export interface PractitionerAvailability {
	overrides: Record<UserUuid, ScheduleOverride[]>;
	blocks: Record<UserUuid, UnavailableBlock[]>;
	clinic_closures: UnavailableBlock[];
}

// Update TeamMember interface to include overrides
export interface TeamMember extends User {
	team_member_info: TeamMemberInfo;
	pivot?: Pivot;
	schedule_overrides?: ScheduleOverride[]; // Add this
}

// Authoritative clinical-user seat usage, as reported by GET /admin/seats.
// `enforced` is false when no active subscription carries a seat allowance —
// treat that as "no limit" and present no quota at all.
export interface SeatUsage {
	used: number;
	total: number;
	available: number;
	enforced: boolean;
}